import { Extension } from '@tiptap/core';

export const MAX_BOOK_INDENT = 8;

const BLOCK_INDENT_TYPES = new Set(['paragraph', 'heading']);
const LIST_INDENT_TYPES = new Set(['listItem']);

export const normalizeBookIndent = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_BOOK_INDENT
    ? parsed
    : 0;
};

const selectedNodePositions = (state, types) => {
  const positions = new Set();
  const { selection, doc } = state;

  if (selection.empty) {
    for (let depth = selection.$from.depth; depth > 0; depth -= 1) {
      const node = selection.$from.node(depth);
      if (types.has(node.type.name)) {
        positions.add(selection.$from.before(depth));
        break;
      }
    }
    return positions;
  }

  doc.nodesBetween(selection.from, selection.to, (node, position) => {
    if (types.has(node.type.name)) positions.add(position);
  });
  return positions;
};

const selectedBlockPositions = (state) => {
  const listPositions = selectedNodePositions(state, LIST_INDENT_TYPES);
  return listPositions.size
    ? listPositions
    : selectedNodePositions(state, BLOCK_INDENT_TYPES);
};

const changeIndent = (delta) => ({ state, dispatch }) => {
  const positions = selectedBlockPositions(state);
  if (!positions.size) return false;

  const transaction = state.tr;
  let changed = false;
  positions.forEach((position) => {
    const node = transaction.doc.nodeAt(position);
    if (!node) return;
    const current = normalizeBookIndent(node.attrs.bookIndent);
    const next = Math.min(
      MAX_BOOK_INDENT,
      Math.max(0, current + delta)
    );
    if (next === current) return;
    transaction.setNodeMarkup(position, undefined, {
      ...node.attrs,
      bookIndent: next,
    });
    changed = true;
  });

  if (!changed) return false;
  if (dispatch) dispatch(transaction);
  return true;
};

const BookIndent = Extension.create({
  name: 'bookIndent',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'listItem'],
        attributes: {
          bookIndent: {
            default: 0,
            parseHTML: (element) =>
              normalizeBookIndent(element.getAttribute('data-book-indent')),
            renderHTML: ({ bookIndent }) => {
              const level = normalizeBookIndent(bookIndent);
              return level
                ? { 'data-book-indent': String(level) }
                : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      increaseBookIndent: () => changeIndent(1),
      decreaseBookIndent: () => changeIndent(-1),
    };
  },
});

export const applyBookIndent = (editor, direction) => {
  if (!editor) return false;
  const increasing = direction === 'increase';

  if (editor.isActive('listItem')) {
    if (increasing) {
      if (editor.chain().focus().sinkListItem('listItem').run()) return true;
      return editor.chain().focus().increaseBookIndent().run();
    }

    if (editor.chain().focus().decreaseBookIndent().run()) return true;
    if (editor.chain().focus().liftListItem('listItem').run()) return true;
  }

  const blockCommand = increasing
    ? 'increaseBookIndent'
    : 'decreaseBookIndent';
  return editor.chain().focus()[blockCommand]().run();
};

export default BookIndent;
